/**
 * Consentimientos List Component
 *
 * Lista de consentimientos informados de un paciente.
 * Incluye acciones para ver, eliminar y activar/desactivar.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, Eye, Trash2, FileCheck, FileX, Loader2, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { useConsentimientos, useDeleteConsentimiento } from "@/hooks/use-consentimientos";
import { useApiError } from "@/hooks/use-api-error";
import { ConsentimientoFormDialog } from "./consentimiento-form-dialog";
import { ConsentimientoViewerDialog } from "./consentimiento-viewer-dialog";

interface ConsentimientosListProps {
  pacienteId: number;
  pacienteNombre: string;
}

export function ConsentimientosList({ pacienteId, pacienteNombre }: ConsentimientosListProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch consentimientos for this patient
  const { data, isLoading, refetch } = useConsentimientos({
    paciente: pacienteId,
    page_size: 100,
    ordering: "-fecha_firma",
  });

  const deleteMutation = useDeleteConsentimiento({
    onSuccess: () => {
      setDeleteId(null);
      refetch();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return dateString;
    }
  };

  const consentimientos = data?.results || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg">
            {t("consentimientos.title", "Consentimientos Informados")}
          </CardTitle>
          <CardDescription>
            {t("consentimientos.description", "Historial de consentimientos del paciente")}
          </CardDescription>
        </div>
        <Button onClick={() => setShowFormDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t("consentimientos.new", "Nuevo")}
        </Button>
      </CardHeader>

      <CardContent>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive mb-4 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : consentimientos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileX className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>{t("consentimientos.noConsents", "No hay consentimientos registrados")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {consentimientos.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                {/* Icon */}
                <div className="flex-shrink-0">
                  {item.tiene_firma ? (
                    <FileCheck className="h-8 w-8 text-green-600" />
                  ) : (
                    <FileX className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.tipo_display}</p>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>{item.firmante}</span>
                    <span>•</span>
                    <span>{formatDate(item.fecha_firma)}</span>
                    <Badge variant={item.aceptado ? "default" : "destructive"} className="text-xs">
                      {item.aceptado
                        ? t("consentimientos.accepted", "Aceptado")
                        : t("consentimientos.rejected", "Rechazado")}
                    </Badge>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setViewingId(item.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("common.view", "Ver")}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDeleteId(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("common.delete", "Eliminar")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Form Dialog */}
      <ConsentimientoFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        pacienteId={pacienteId}
        pacienteNombre={pacienteNombre}
        onSuccess={() => {
          setShowFormDialog(false);
          refetch();
        }}
      />

      {/* Viewer Dialog */}
      <ConsentimientoViewerDialog
        open={!!viewingId}
        onOpenChange={(open: boolean) => !open && setViewingId(null)}
        consentimientoId={viewingId}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("consentimientos.deleteTitle", "¿Eliminar consentimiento?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "consentimientos.deleteDescription",
                "Esta acción no se puede deshacer. El consentimiento será eliminado permanentemente."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("common.delete", "Eliminar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
